"use client";

import React, { useEffect, useMemo, useState } from "react";

const COLORS = {
  page: "#f4f6f8",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#d9e0e7",
  borderStrong: "#c4ced8",
  text: "#1f2937",
  textMuted: "#64748b",
  textSoft: "#94a3b8",
  primary: "#2563eb",
  primaryDark: "#1d4ed8",
  primarySoft: "#eff6ff",
  success: "#15803d",
  successSoft: "#f0fdf4",
  successBorder: "#bbf7d0",
  danger: "#dc2626",
  dangerDark: "#b91c1c",
  dangerSoft: "#fef2f2",
  dangerBorder: "#fecaca",
  warning: "#b45309",
  warningSoft: "#fffbeb",
  warningBorder: "#fde68a",
};

const extractYear = (value) => {
  if (!value) return null;
  const stringValue = String(value);
  const matches = stringValue.match(/\b(20\d{2}|19\d{2})\b/g);
  return matches?.[matches.length - 1] || null;
};

export default function TrialBalancePage({ globalFile, globalSheet }) {
  const [localFile, setLocalFile] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [entityId, setEntityId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [showAuditLog, setShowAuditLog] = useState(true);

  const file = globalFile || localFile;
  const effectiveSheet = selectedSheet || globalSheet || "";

  const runInspection = async (fileToInspect, sheetToUse) => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    setPeriods([]);
    setSelectedPeriod(null);
    setSelectedYear("");

    const formData = new FormData();
    formData.append("file", fileToInspect);

    if (sheetToUse) {
      formData.append("sheet", sheetToUse);
    }

    try {
      const res = await fetch("/api/inspect", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to inspect spreadsheet.");
      }

      setSheetNames(data.sheetNames || []);
      setSelectedSheet(data.activeSheetName || "");
      setEntityId(data.detectedEntityId || "");
      setPeriods(data.periods || []);

      if (data.periods?.length > 0) {
        setSelectedPeriod(data.periods[0]);

        const firstYear =
          extractYear(data.periods[0].period) ||
          extractYear(data.periods[0].label);

        if (firstYear) {
          setSelectedYear(firstYear);
        }
      }
    } catch (err) {
      setErrorMsg(err?.message || "An unexpected inspection error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    if (globalFile) {
      const sheetToUse = globalSheet || "";
      const formData = new FormData();
      formData.append("file", globalFile);
      if (sheetToUse) formData.append("sheet", sheetToUse);

      fetch("/api/inspect", {
        method: "POST",
        body: formData,
      })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to inspect spreadsheet.");
          return res.json();
        })
        .then((data) => {
          if (ignore) return;
          setSheetNames(data.sheetNames || []);
          setSelectedSheet(data.activeSheetName || "");
          setEntityId(data.detectedEntityId || "");
          setPeriods(data.periods || []);

          if (data.periods?.length > 0) {
            setSelectedPeriod(data.periods[0]);
            const firstYear =
              extractYear(data.periods[0].period) ||
              extractYear(data.periods[0].label);
            if (firstYear) setSelectedYear(firstYear);
          }
        })
        .catch((err) => {
          if (ignore) return;
          setErrorMsg(err?.message || "An unexpected inspection error occurred.");
        });
    }

    return () => {
      ignore = true;
    };
  }, [globalFile, globalSheet]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setLocalFile(selectedFile);
    await runInspection(selectedFile, "");
  };

  const handleSheetChange = (e) => {
    const sheet = e.target.value;
    setSelectedSheet(sheet);

    if (file) {
      runInspection(file, sheet);
    }
  };

  const handleImport = async (periodObj) => {
    if (!file || !entityId) {
      setErrorMsg("Entity ID and File are required before importing.");
      return;
    }

    if (!periodObj.isCleared) {
      setErrorMsg(
        `Cannot import: ${periodObj.label} (${periodObj.period}) is not cleared.`,
      );
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("entityId", entityId);
    formData.append("period", periodObj.period);
    formData.append("sheet", effectiveSheet);

    try {
      const res = await fetch("/api/import", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Import failed.");
      }

      setSuccessMsg(
        `Successfully imported ${periodObj.label} (v${data.version}) with ${data.rowCount} rows.`,
      );
    } catch (err) {
      setErrorMsg(err?.message || "Import failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const yearGroups = useMemo(() => {
    const groups = new Map();

    periods.forEach((period) => {
      const year =
        extractYear(period.period) || extractYear(period.label) || "Other";

      if (!groups.has(year)) {
        groups.set(year, []);
      }

      groups.get(year).push(period);
    });

    return Array.from(groups.entries()).sort(([yearA], [yearB]) => {
      if (yearA === "Other") return 1;
      if (yearB === "Other") return -1;

      return Number(yearB) - Number(yearA);
    });
  }, [periods]);

  const activeYearPeriods = useMemo(() => {
    if (!yearGroups.length) return [];

    const group = yearGroups.find(
      ([year]) => String(year) === String(selectedYear),
    );

    return group?.[1] || yearGroups[0][1];
  }, [yearGroups, selectedYear]);

  const unclearedPeriods = periods.filter((period) => !period.isCleared);
  const clearedCount = periods.length - unclearedPeriods.length;
  const selectedIsCleared = selectedPeriod?.isCleared ?? false;

  const formatNumber = (value) =>
    Number(value || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDifference = (value) =>
    Number(value || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getPeriodShortLabel = (period) => {
    if (!period?.label) return period?.period || "";
    const words = String(period.label).trim().split(/\s+/);
    return words[0].slice(0, 3).toUpperCase();
  };

  const controlLabelStyle = {
    display: "block",
    marginBottom: "5px",
    fontSize: "10px",
    lineHeight: 1.2,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const inputStyle = {
    height: "34px",
    padding: "0 10px",
    borderRadius: "6px",
    border: `1px solid ${COLORS.border}`,
    background: COLORS.surface,
    color: COLORS.text,
    fontSize: "12px",
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.page,
        padding: "20px",
        color: COLORS.text,
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: "1450px",
          margin: "0 auto",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "20px",
            marginBottom: "14px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "22px",
                lineHeight: 1.15,
                fontWeight: 700,
                color: COLORS.text,
                marginBottom: "4px",
                letterSpacing: "-0.02em",
              }}
            >
              Trial Balance Clearance
            </div>

            <div
              style={{
                fontSize: "12px",
                color: COLORS.textMuted,
              }}
            >
              Review, clear, and import trial balance snapshots by reporting
              period.
            </div>
          </div>

          {periods.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  padding: "7px 10px",
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.surface,
                  borderRadius: "6px",
                  fontSize: "11px",
                  color: COLORS.textMuted,
                }}
              >
                <strong style={{ color: COLORS.text }}>{periods.length}</strong>{" "}
                periods
              </div>

              <div
                style={{
                  padding: "7px 10px",
                  border: `1px solid ${COLORS.successBorder}`,
                  background: COLORS.successSoft,
                  borderRadius: "6px",
                  fontSize: "11px",
                  color: COLORS.success,
                }}
              >
                <strong>{clearedCount}</strong> cleared
              </div>

              <div
                style={{
                  padding: "7px 10px",
                  border: `1px solid ${COLORS.dangerBorder}`,
                  background: COLORS.dangerSoft,
                  borderRadius: "6px",
                  fontSize: "11px",
                  color: COLORS.dangerDark,
                }}
              >
                <strong>{unclearedPeriods.length}</strong> unresolved
              </div>
            </div>
          )}
        </div>

        {/* CONTROLS */}
        <div
          style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "10px",
            padding: "14px",
            marginBottom: "12px",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "14px",
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!globalFile && (
              <div>
                <label style={controlLabelStyle}>Workbook</label>

                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  style={{
                    fontSize: "12px",
                    color: COLORS.text,
                    maxWidth: "270px",
                  }}
                />
              </div>
            )}

            {sheetNames.length > 1 && (
              <div>
                <label style={controlLabelStyle}>Sheet</label>

                <select
                  value={effectiveSheet}
                  onChange={handleSheetChange}
                  style={{
                    ...inputStyle,
                    minWidth: "180px",
                    color:
                      effectiveSheet === globalSheet
                        ? COLORS.text
                        : COLORS.warning,
                    borderColor:
                      effectiveSheet === globalSheet
                        ? COLORS.border
                        : COLORS.warningBorder,
                  }}
                >
                  {sheetNames.map((sheet) => (
                    <option key={sheet} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={controlLabelStyle}>Entity ID</label>

              <input
                type="text"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder="Entity Code"
                style={{
                  ...inputStyle,
                  width: "180px",
                }}
              />
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {loading && (
                <div
                  style={{
                    padding: "7px 10px",
                    borderRadius: "6px",
                    background: COLORS.primarySoft,
                    color: COLORS.primary,
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  Scanning workbook...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ALERTS */}
        {errorMsg && (
          <div
            style={{
              padding: "10px 12px",
              background: COLORS.dangerSoft,
              border: `1px solid ${COLORS.dangerBorder}`,
              color: COLORS.dangerDark,
              borderRadius: "7px",
              marginBottom: "10px",
              fontSize: "12px",
            }}
          >
            <strong>Error:</strong> {errorMsg}
          </div>
        )}

        {successMsg && (
          <div
            style={{
              padding: "10px 12px",
              background: COLORS.successSoft,
              border: `1px solid ${COLORS.successBorder}`,
              color: COLORS.success,
              borderRadius: "7px",
              marginBottom: "10px",
              fontSize: "12px",
            }}
          >
            <strong>Success:</strong> {successMsg}
          </div>
        )}

        {unclearedPeriods.length > 0 && (
          <div
            style={{
              padding: "10px 12px",
              background: COLORS.warningSoft,
              border: `1px solid ${COLORS.warningBorder}`,
              color: COLORS.warning,
              borderRadius: "7px",
              marginBottom: "12px",
              fontSize: "11px",
            }}
          >
            <strong>
              {unclearedPeriods.length} period
              {unclearedPeriods.length !== 1 ? "s" : ""} require clearance:
            </strong>{" "}
            {unclearedPeriods
              .map((period) => `${period.label} (${period.period})`)
              .join(" • ")}
          </div>
        )}

        {/* PERIOD NAVIGATION */}
        {periods.length > 0 && (
          <div
            style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "10px",
              marginBottom: "12px",
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
          >
            {/* YEAR TABS */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                padding: "8px",
                overflowX: "auto",
                background: COLORS.surfaceAlt,
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  padding: "0 8px",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: COLORS.textSoft,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Year
              </div>

              {yearGroups.map(([year, yearPeriods]) => {
                const active =
                  String(year) ===
                  String(selectedYear || extractYear(periods[0]?.period));

                const unresolvedCount = yearPeriods.filter(
                  (period) => !period.isCleared,
                ).length;

                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => {
                      setSelectedYear(String(year));

                      const firstPeriod = yearPeriods[0];

                      if (
                        firstPeriod &&
                        !yearPeriods.some(
                          (period) => period.period === selectedPeriod?.period,
                        )
                      ) {
                        setSelectedPeriod(firstPeriod);
                      }
                    }}
                    style={{
                      flexShrink: 0,
                      minWidth: "75px",
                      height: "30px",
                      padding: "0 11px",
                      borderRadius: "6px",
                      border: `1px solid ${
                        active ? COLORS.primary : COLORS.border
                      }`,
                      background: active ? COLORS.primary : COLORS.surface,
                      color: active ? "#ffffff" : COLORS.textMuted,
                      fontSize: "11px",
                      fontWeight: active ? 700 : 500,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                    }}
                  >
                    {year}

                    {unresolvedCount > 0 && (
                      <span
                        style={{
                          minWidth: "16px",
                          height: "16px",
                          padding: "0 4px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "9999px",
                          background: active
                            ? "rgba(255,255,255,0.2)"
                            : COLORS.dangerSoft,
                          color: active ? "#ffffff" : COLORS.dangerDark,
                          fontSize: "9px",
                          fontWeight: 700,
                        }}
                      >
                        {unresolvedCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* PERIOD STRIP */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                padding: "9px 10px",
                overflowX: "auto",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  minWidth: "44px",
                  padding: "0 4px",
                  fontSize: "9px",
                  fontWeight: 700,
                  color: COLORS.textSoft,
                  textTransform: "uppercase",
                }}
              >
                Period
              </div>

              {activeYearPeriods.map((period) => {
                const selected = selectedPeriod?.period === period.period;
                const cleared = period.isCleared;
                const shortLabel = getPeriodShortLabel(period);

                return (
                  <button
                    key={period.period}
                    type="button"
                    onClick={() => setSelectedPeriod(period)}
                    style={{
                      flexShrink: 0,
                      minWidth: "58px",
                      height: "38px",
                      padding: "3px 8px",
                      borderRadius: "6px",
                      border: `1px solid ${
                        selected
                          ? COLORS.primary
                          : cleared
                            ? COLORS.successBorder
                            : COLORS.dangerBorder
                      }`,
                      background: selected
                        ? COLORS.primarySoft
                        : cleared
                          ? COLORS.successSoft
                          : COLORS.dangerSoft,
                      color: selected
                        ? COLORS.primaryDark
                        : cleared
                          ? COLORS.success
                          : COLORS.dangerDark,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "1px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {shortLabel}
                    </span>

                    <span
                      style={{
                        fontSize: "8px",
                        fontWeight: 600,
                        opacity: 0.8,
                      }}
                    >
                      {cleared ? "OK" : "CHECK"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* SELECTED PERIOD DETAIL */}
        {selectedPeriod && (
          <div
            style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "10px",
              marginBottom: "12px",
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                padding: "13px 14px",
                borderBottom: `1px solid ${COLORS.border}`,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "3px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: COLORS.text,
                    }}
                  >
                    {selectedPeriod.label}
                  </div>

                  <span
                    style={{
                      padding: "3px 7px",
                      borderRadius: "9999px",
                      background: selectedIsCleared
                        ? COLORS.successSoft
                        : COLORS.dangerSoft,
                      border: `1px solid ${
                        selectedIsCleared
                          ? COLORS.successBorder
                          : COLORS.dangerBorder
                      }`,
                      color: selectedIsCleared
                        ? COLORS.success
                        : COLORS.dangerDark,
                      fontSize: "9px",
                      fontWeight: 700,
                    }}
                  >
                    {selectedIsCleared ? "CLEARED" : "NOT CLEARED"}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    color: COLORS.textMuted,
                  }}
                >
                  {selectedPeriod.period}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {!selectedIsCleared && (
                  <div
                    style={{
                      padding: "7px 10px",
                      background: COLORS.dangerSoft,
                      border: `1px solid ${COLORS.dangerBorder}`,
                      borderRadius: "6px",
                      fontSize: "11px",
                      color: COLORS.dangerDark,
                      fontWeight: 600,
                    }}
                  >
                    Difference: {formatDifference(selectedPeriod.difference)}
                  </div>
                )}

                <button
                  type="button"
                  disabled={!selectedIsCleared || submitting}
                  onClick={() => handleImport(selectedPeriod)}
                  style={{
                    height: "34px",
                    padding: "0 14px",
                    border: "none",
                    borderRadius: "6px",
                    background: selectedIsCleared ? COLORS.success : "#cbd5e1",
                    color: selectedIsCleared ? "#ffffff" : "#64748b",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor:
                      selectedIsCleared && !submitting
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  {submitting
                    ? "Importing..."
                    : selectedIsCleared
                      ? "Import Snapshot"
                      : "Cannot Import"}
                </button>
              </div>
            </div>

            {/* CLEARANCE SUMMARY */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "1px",
                background: COLORS.border,
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <div
                style={{
                  background: selectedIsCleared
                    ? COLORS.successSoft
                    : COLORS.dangerSoft,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: selectedIsCleared
                      ? COLORS.success
                      : COLORS.dangerDark,
                    marginBottom: "4px",
                  }}
                >
                  Clearance
                </div>

                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: selectedIsCleared
                      ? COLORS.success
                      : COLORS.dangerDark,
                  }}
                >
                  {selectedIsCleared ? "Balanced" : "Out of Balance"}
                </div>
              </div>

              <div
                style={{
                  background: COLORS.surface,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: COLORS.textMuted,
                    marginBottom: "4px",
                  }}
                >
                  Debit
                </div>

                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: COLORS.text,
                  }}
                >
                  {formatNumber(selectedPeriod.totalDebit)}
                </div>
              </div>

              <div
                style={{
                  background: COLORS.surface,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: COLORS.textMuted,
                    marginBottom: "4px",
                  }}
                >
                  Credit
                </div>

                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: COLORS.text,
                  }}
                >
                  {formatNumber(selectedPeriod.totalCredit)}
                </div>
              </div>

              <div
                style={{
                  background: COLORS.surface,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: COLORS.textMuted,
                    marginBottom: "4px",
                  }}
                >
                  Difference
                </div>

                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color:
                      Math.abs(Number(selectedPeriod.difference || 0)) < 0.01
                        ? COLORS.success
                        : COLORS.danger,
                  }}
                >
                  {formatNumber(selectedPeriod.difference)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AUDIT LOG */}
        {selectedPeriod?.notepadContent && (
          <div
            style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
          >
            <button
              type="button"
              onClick={() => setShowAuditLog((current) => !current)}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "11px 14px",
                background: COLORS.surface,
                border: "none",
                borderBottom: showAuditLog
                  ? `1px solid ${COLORS.border}`
                  : "none",
                cursor: "pointer",
                color: COLORS.text,
                textAlign: "left",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  Audit Log &amp; Calculation
                </div>

                <div
                  style={{
                    marginTop: "2px",
                    fontSize: "10px",
                    color: COLORS.textMuted,
                  }}
                >
                  {selectedPeriod.label} · {selectedPeriod.period}
                </div>
              </div>

              <span
                style={{
                  fontSize: "11px",
                  color: COLORS.textMuted,
                }}
              >
                {showAuditLog ? "Hide" : "Show"}
              </span>
            </button>

            {showAuditLog && (
              <textarea
                readOnly
                value={selectedPeriod.notepadContent}
                style={{
                  width: "100%",
                  height: "340px",
                  display: "block",
                  padding: "14px",
                  border: "none",
                  outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box",
                  background: "#f8fafc",
                  color: "#334155",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: "11px",
                  lineHeight: 1.55,
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}